import React, { useState } from 'react';
import './NewsletterSubscribe.css';

const LIST_OPTIONS = [
  {
    id: 'studio',
    label: 'The PP Studio',
    blurb: 'Contract tips, business insight, and the occasional straight-talk.',
  },
  {
    id: 'pp_edit',
    label: 'The PP Edit',
    blurb: 'Personal stories, reflections, and the quiet moments in between.',
  },
];

const listNamesJoined = (ids) => {
  const names = LIST_OPTIONS.filter(o => ids.includes(o.id)).map(o => o.label);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

function NewsletterSubscribe({ defaultLists = ['pp_edit'], theme = 'edit', kicker, title, subtitle }) {
  const [lists, setLists] = useState(defaultLists);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('form'); // form | otp | done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmedLists, setConfirmedLists] = useState([]);

  const toggleList = (id) => {
    setLists(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    if (loading) return;
    if (lists.length === 0) {
      setError('Choose at least one newsletter.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/newsletter/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), lists }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      if (data.alreadySubscribed) {
        setConfirmedLists(data.lists || lists);
        setStep('done');
      } else {
        setStep('otp');
      }
    } catch (err) {
      setError(err.message || 'Enter a valid email — try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/newsletter/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      setConfirmedLists(data.lists || lists);
      setStep('done');
    } catch (err) {
      setError(err.message || "That code didn't work — try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('form');
    setEmail('');
    setCode('');
    setError('');
  };

  return (
    <section className={`nl-subscribe nl-subscribe--${theme}`} aria-labelledby="nl-subscribe-title">
      <div className="nl-subscribe-inner">
        {kicker && <span className="nl-subscribe-kicker">{kicker}</span>}
        <h2 id="nl-subscribe-title" className="nl-subscribe-title">{title}</h2>
        {subtitle && <p className="nl-subscribe-subtitle">{subtitle}</p>}

        {step === 'form' && (
          <form className="nl-subscribe-form" onSubmit={requestCode}>
            <div className="nl-subscribe-lists" role="group" aria-label="Choose newsletters">
              {LIST_OPTIONS.map(opt => (
                <label key={opt.id} className={`nl-subscribe-list-opt${lists.includes(opt.id) ? ' checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={lists.includes(opt.id)}
                    onChange={() => toggleList(opt.id)}
                  />
                  <span className="nl-subscribe-list-text">
                    <strong>{opt.label}</strong>
                    <em>{opt.blurb}</em>
                  </span>
                </label>
              ))}
            </div>
            <div className="nl-subscribe-input-row">
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email"
                aria-label="Email address"
                disabled={loading}
                className="nl-subscribe-input"
              />
              <button type="submit" className="nl-subscribe-btn" disabled={loading}>
                {loading ? 'Sending…' : 'Subscribe'}
              </button>
            </div>
            {error && <p className="nl-subscribe-error">{error}</p>}
          </form>
        )}

        {step === 'otp' && (
          <form className="nl-subscribe-form nl-subscribe-otp-form" onSubmit={verifyCode}>
            <p className="nl-subscribe-otp-note">
              We sent a 6-digit code to <strong>{email}</strong> for {listNamesJoined(lists)}. It expires in 10 minutes.
            </p>
            <div className="nl-subscribe-input-row">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                aria-label="6-digit code"
                disabled={loading}
                className="nl-subscribe-input nl-subscribe-otp-input"
              />
              <button type="submit" className="nl-subscribe-btn" disabled={loading || code.length !== 6}>
                {loading ? 'Checking…' : 'Confirm'}
              </button>
            </div>
            {error && <p className="nl-subscribe-error">{error}</p>}
            <div className="nl-subscribe-otp-actions">
              <button type="button" className="nl-subscribe-link-btn" onClick={requestCode} disabled={loading}>
                Resend code
              </button>
              <button type="button" className="nl-subscribe-link-btn" onClick={reset} disabled={loading}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <p className="nl-subscribe-done">
            You're in — welcome to {listNamesJoined(confirmedLists)}.
          </p>
        )}
      </div>
    </section>
  );
}

export default NewsletterSubscribe;
